import { useTheme } from '../../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SettingsPanel({ isOpen, onClose }: SettingsPanelProps) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 z-40 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-80 shadow-2xl transform transition-transform duration-300 ease-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${
          theme === 'dark' ? 'bg-[#1A0B2E]' : 'bg-gray-50'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          theme === 'dark' ? 'border-white/10' : 'border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Configuración</h2>
          <button
            onClick={onClose}
            className={`w-8 h-8 rounded-full transition-colors flex items-center justify-center ${
              theme === 'dark' 
                ? 'bg-white/5 hover:bg-white/10' 
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            <i className={`ri-close-line text-xl ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6 overflow-y-auto h-[calc(100%-73px)]">
          {/* Apariencia Section */}
          <div>
            <h3 className={`text-xs font-medium uppercase tracking-wider mb-3 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-500'
            }`}>
              Apariencia
            </h3>
            
            {/* Tema Button */}
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                theme === 'dark'
                  ? 'bg-white/5 hover:bg-white/10'
                  : 'bg-white hover:bg-gray-100 shadow-sm'
              }`}
            >
              <div className="flex items-center gap-3">
                <i className="ri-palette-line text-purple-400 text-xl"></i>
                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Tema</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {theme === 'dark' ? 'Oscuro' : 'Claro'}
                </span>
                <i className={`ri-arrow-right-s-line ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}></i>
              </div>
            </button>
          </div>

          {/* Chat Section */}
          <div>
            <h3 className={`text-xs font-medium uppercase tracking-wider mb-3 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-500'
            }`}>
              Chat
            </h3>
            
            <div className="space-y-2">
              <button className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                theme === 'dark'
                  ? 'bg-white/5 hover:bg-white/10'
                  : 'bg-white hover:bg-gray-100 shadow-sm'
              }`}>
                <div className="flex items-center gap-3">
                  <i className="ri-delete-bin-line text-red-400 text-xl"></i>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Borrar historial</span>
                </div>
                <i className={`ri-arrow-right-s-line ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}></i>
              </button>

              <button className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                theme === 'dark'
                  ? 'bg-white/5 hover:bg-white/10'
                  : 'bg-white hover:bg-gray-100 shadow-sm'
              }`}>
                <div className="flex items-center gap-3">
                  <i className="ri-download-line text-blue-400 text-xl"></i>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Exportar conversaciones</span>
                </div>
                <i className={`ri-arrow-right-s-line ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}></i>
              </button>
            </div>
          </div>

          {/* Cuenta Section */}
          <div>
            <h3 className={`text-xs font-medium uppercase tracking-wider mb-3 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-500'
            }`}>
              Cuenta
            </h3>
            
            <div className="space-y-2">
              <button className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                theme === 'dark'
                  ? 'bg-white/5 hover:bg-white/10'
                  : 'bg-white hover:bg-gray-100 shadow-sm'
              }`}>
                <div className="flex items-center gap-3">
                  <i className="ri-user-line text-green-400 text-xl"></i>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Perfil</span>
                </div>
                <i className={`ri-arrow-right-s-line ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}></i>
              </button>

              <button className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                theme === 'dark'
                  ? 'bg-white/5 hover:bg-white/10'
                  : 'bg-white hover:bg-gray-100 shadow-sm'
              }`}>
                <div className="flex items-center gap-3">
                  <i className="ri-lock-line text-yellow-400 text-xl"></i>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Privacidad</span>
                </div>
                <i className={`ri-arrow-right-s-line ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}></i>
              </button>
            </div>
          </div>

          {/* Acerca de Section */}
          <div>
            <h3 className={`text-xs font-medium uppercase tracking-wider mb-3 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-500'
            }`}>
              Acerca de
            </h3>
            
            <div className="space-y-2">
              <button className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                theme === 'dark'
                  ? 'bg-white/5 hover:bg-white/10'
                  : 'bg-white hover:bg-gray-100 shadow-sm'
              }`}>
                <div className="flex items-center gap-3">
                  <i className="ri-information-line text-cyan-400 text-xl"></i>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Información</span>
                </div>
                <span className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>v1.0.0</span>
              </button>

              <button className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                theme === 'dark'
                  ? 'bg-white/5 hover:bg-white/10'
                  : 'bg-white hover:bg-gray-100 shadow-sm'
              }`}>
                <div className="flex items-center gap-3">
                  <i className="ri-question-line text-orange-400 text-xl"></i>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Ayuda</span>
                </div>
                <i className={`ri-arrow-right-s-line ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}></i>
              </button>

              <button
                onClick={() => {
                  navigate('/legal');
                  onClose();
                }}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                  theme === 'dark'
                    ? 'bg-white/5 hover:bg-white/10'
                    : 'bg-white hover:bg-gray-100 shadow-sm'
                }`}
              >
                <div className="flex items-center gap-3">
                  <i className="ri-file-shield-line text-purple-400 text-xl"></i>
                  <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Legal y Privacidad</span>
                </div>
                <i className={`ri-arrow-right-s-line ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
